/*
 * WebGL view of the live case: the 27 EU member states as a
 * constellation, a shield that seals the reporter's identity at the
 * origin and the WBlow inbox floating above the map. The DOM timeline
 * in case.js drives it phase by phase.
 */
import G from '../adam/gl';

// [code, capital, lat, lon]
export const STATES = [
  ["AT", "Vienna", 48.21, 16.37], ["BE", "Brussels", 50.85, 4.35], ["BG", "Sofia", 42.70, 23.32],
  ["HR", "Zagreb", 45.81, 15.98], ["CY", "Nicosia", 35.19, 33.38], ["CZ", "Prague", 50.08, 14.44],
  ["DK", "Copenhagen", 55.68, 12.57], ["EE", "Tallinn", 59.44, 24.75], ["FI", "Helsinki", 60.17, 24.94],
  ["FR", "Paris", 48.86, 2.35], ["DE", "Berlin", 52.52, 13.40], ["GR", "Athens", 37.98, 23.73],
  ["HU", "Budapest", 47.50, 19.04], ["IE", "Dublin", 53.35, -6.26], ["IT", "Rome", 41.90, 12.50],
  ["LV", "Riga", 56.95, 24.11], ["LT", "Vilnius", 54.69, 25.28], ["LU", "Luxembourg", 49.61, 6.13],
  ["MT", "Valletta", 35.90, 14.51], ["NL", "Amsterdam", 52.37, 4.90], ["PL", "Warsaw", 52.23, 21.01],
  ["PT", "Lisbon", 38.72, -9.14], ["RO", "Bucharest", 44.43, 26.10], ["SK", "Bratislava", 48.15, 17.11],
  ["SI", "Ljubljana", 46.06, 14.51], ["ES", "Madrid", 40.42, -3.70], ["SE", "Stockholm", 59.33, 18.07]
];

export function createCaseScene(canvas, labelRoot) {
  const stage = G.createStage(canvas, { fov: 36, z: 9, y: 1.4, fit: 1.45 });
  if (!stage) return null;

  const { THREE, palette, rand } = G;
  const V3 = THREE.Vector3;

  const TILT = -0.95;
  const root = new THREE.Group();
  root.rotation.x = TILT;
  root.position.y = -0.55;
  stage.scene.add(root);

  // Flat projection centred on the EU; the map lies in the group's XY plane
  const nodes = STATES.map(([, , lat, lon]) => new V3((lon - 12) * 0.114, (lat - 48.5) * 0.17, 0));
  const VAULT = new V3(0.25, -0.35, 2.8);

  /*
   * Landmass dust around each capital, so the constellation reads as Europe.
   */
  const gauss = () => rand() + rand() + rand() - 1.5;
  const DUST = 1500;
  const dust = G.glowPoints(stage, DUST);
  for (let i = 0; i < DUST; i++) {
    const n = nodes[Math.floor(Math.random() * nodes.length)];
    dust.setPosition(i, new V3(n.x + gauss() * 0.42, n.y + gauss() * 0.34, gauss() * 0.03));
    dust.setColor(i, i % 7 ? palette.blue : palette.cyan, rand(0.35, 0.7));
    dust.size[i] = rand(0.02, 0.045);
    dust.alpha[i] = rand(0.2, 0.6);
  }
  dust.flush();
  root.add(dust.points);

  const capitals = G.glowPoints(stage, nodes.length);
  nodes.forEach((n, i) => capitals.setPosition(i, n));
  root.add(capitals.points);

  // Each capital wired to its nearest neighbours
  const edges = [];
  nodes.forEach((a, ai) => {
    nodes
      .map((b, bi) => ({ bi, d: a.distanceTo(b) }))
      .filter(item => item.bi !== ai)
      .sort((x, y) => x.d - y.d)
      .slice(0, 3)
      .forEach(({ bi }) => {
        if (!edges.some(([x, y]) => (x === bi && y === ai) || (x === ai && y === bi))) edges.push([ai, bi]);
      });
  });
  const network = G.glowLines(edges.length);
  edges.forEach(([a, b], i) => network.setSegment(i, nodes[a], nodes[b], palette.blue, 0.16));
  network.flush();
  root.add(network.lines);

  root.add(G.circle(3.3, palette.blue, 0.07));
  root.add(G.circle(2.1, palette.blue, 0.05));

  /*
   * The inbox: a wireframe core with an orbit of archived cases.
   */
  const vault = new THREE.Group();
  vault.position.copy(VAULT);
  root.add(vault);

  const core = G.wireframe(new THREE.OctahedronGeometry(0.38, 0), palette.cyan, 0.6);
  const inner = G.wireframe(new THREE.IcosahedronGeometry(0.2, 0), palette.violet, 0.45);
  vault.add(core, inner, G.circle(0.66, palette.cyan, 0.22), G.circle(0.86, palette.violet, 0.1));

  const halo = G.glowPoints(stage, 1);
  halo.setColor(0, palette.cyan);
  halo.size[0] = 1.1;
  halo.alpha[0] = 0.35;
  halo.flush();
  vault.add(halo.points);

  const ARCHIVE = 56;
  const archive = G.glowPoints(stage, ARCHIVE);
  for (let i = 0; i < ARCHIVE; i++) {
    archive.setColor(i, i % 3 ? palette.cyan : palette.violet, rand(0.7, 1));
    archive.size[i] = rand(0.05, 0.08);
    archive.alpha[i] = 0;
  }
  vault.add(archive.points);
  let archived = 14;

  // A faint beam anchors the inbox to the map
  const beam = G.glowLines(1);
  beam.setSegment(0, VAULT, new V3(VAULT.x, VAULT.y, 0), palette.cyan, 0.05, palette.cyan, 0.2);
  beam.flush();
  root.add(beam.lines);

  /*
   * Shield around the reporter, with their identity sealed inside.
   */
  const shield = new THREE.Group();
  shield.visible = false;
  root.add(shield);

  const dome = G.wireframe(new THREE.IcosahedronGeometry(0.4, 1), palette.cyan, 0.4);
  const identityRing = G.circle(0.2, palette.amber, 0.6);
  shield.add(dome, identityRing);

  const identity = G.glowPoints(stage, 1);
  identity.setColor(0, palette.amber);
  identity.setPosition(0, new V3(0, 0, 0.05));
  identity.size[0] = 0.34;
  identity.flush();
  shield.add(identity.points);

  /*
   * The secure channel between origin and inbox, drawn as the report travels.
   */
  const ARC = 64;
  const arc = G.glowLines(ARC);
  root.add(arc.lines);

  const arcFrom = new V3();
  const arcControl = new V3();
  const arcPoint = (t, out) => G.bezier(out, arcFrom, arcControl, VAULT, t);

  /*
   * Packets moving along the channel, each with a short trail.
   */
  const TRAIL = 8;
  const FLIGHTS = 10;
  const packets = G.glowPoints(stage, FLIGHTS * TRAIL);
  for (let i = 0; i < packets.count; i++) packets.alpha[i] = 0;
  root.add(packets.points);
  const flights = [];

  function fly(fromVault, duration, color, size, onArrive) {
    if (flights.length >= FLIGHTS) flights.shift();
    flights.push({ fromVault, duration, color, size, onArrive, t: 0 });
  }

  /*
   * Attempts to expose the reporter: they close in on the shield and bounce off.
   */
  const THREATS = 26;
  const threats = G.glowPoints(stage, THREATS);
  const threatState = Array.from({ length: THREATS }, () => ({ life: 0, pos: new V3(), vel: new V3(), bounced: false }));
  for (let i = 0; i < THREATS; i++) {
    threats.setColor(i, palette.pink);
    threats.size[i] = 0.09;
    threats.alpha[i] = 0;
  }
  root.add(threats.points);

  const pulses = Array.from({ length: 4 }, () => {
    const ring = G.circle(1, palette.cyan, 0);
    ring.visible = false;
    root.add(ring);
    return { ring, t: 1 };
  });

  function pulse(at, color, size = 1) {
    const p = pulses.find(item => item.t >= 1) || pulses[0];
    p.t = 0;
    p.size = size;
    p.ring.position.copy(at);
    p.ring.material.color.copy(color);
    p.ring.visible = true;
  }

  /*
   * Screen-space labels: a code per country, the origin, the inbox and the sealed identity.
   */
  function label(className, html, color) {
    const el = document.createElement("div");
    el.className = `scene-label ${className}`;
    el.innerHTML = `<span>${html}</span>`;
    if (color) el.style.setProperty("--c", color);
    labelRoot.append(el);
    return el;
  }

  const css = color => "#" + color.getHexString();
  const codeLabels = STATES.map(([code]) => label("code", code));
  const originLabel = label("origin hide", "", css(palette.amber));
  const vaultLabel = label("", "WBlow inbox", css(palette.cyan));
  const sealLabel = label("hide", "Identity sealed", css(palette.violet));

  const state = {
    origin: -1,
    shield: 0,
    shieldTarget: 0,
    arcDrawn: 0,
    arcDraw: false,
    arcAlpha: 0,
    arcAlphaTarget: 0,
    threats: false,
    threatClock: 0,
    flash: 0
  };

  const tmp = new V3();
  const tmp2 = new V3();
  const LABEL_LIFT = new V3(0, 0, 0.62);

  stage.start((dt, time) => {
    root.rotation.x = TILT + stage.pointer.y * 0.05;
    root.rotation.z = Math.sin(time * 0.12) * 0.05 - stage.pointer.x * 0.07;
    root.updateMatrixWorld(true);

    core.rotation.y += dt * 0.5;
    core.rotation.x += dt * 0.2;
    inner.rotation.y -= dt * 0.8;
    halo.alpha[0] = 0.3 + Math.sin(time * 2) * 0.06;
    halo.flush();

    // Capitals: the origin glows amber
    nodes.forEach((n, i) => {
      const active = i === state.origin;
      capitals.setColor(i, active ? palette.amber : palette.blue, active ? 1 : 0.85);
      capitals.size[i] = active ? 0.22 + Math.sin(time * 4) * 0.03 : 0.1;
      capitals.alpha[i] = active ? 1 : 0.75;
    });
    capitals.flush();

    // Archive orbit
    for (let i = 0; i < ARCHIVE; i++) {
      const a = i / ARCHIVE * Math.PI * 2 + time * 0.15;
      archive.setPosition(i, tmp.set(Math.cos(a) * 0.66, Math.sin(a) * 0.66, 0));
      const target = i < archived ? 0.9 : 0;
      archive.alpha[i] += (target - archive.alpha[i]) * Math.min(1, dt * 3);
    }
    archive.flush();

    // Shield
    state.shield += (state.shieldTarget - state.shield) * Math.min(1, dt * 4);
    state.flash = Math.max(0, state.flash - dt * 2.5);
    shield.visible = state.shield > 0.01;
    shield.scale.setScalar(Math.max(0.001, state.shield));
    dome.rotation.z += dt * 0.3;
    dome.material.opacity = 0.25 + state.flash * 0.5;
    dome.material.color.copy(palette.cyan).lerp(palette.pink, state.flash);
    identityRing.scale.setScalar(1 + Math.sin(time * 3) * 0.08);

    // Channel
    if (state.arcDraw) state.arcDrawn = Math.min(1, state.arcDrawn + dt / 1.5);
    state.arcAlpha += (state.arcAlphaTarget - state.arcAlpha) * Math.min(1, dt * 2.5);
    const drawn = Math.floor(state.arcDrawn * ARC);
    for (let i = 0; i < drawn; i++) {
      arcPoint(i / ARC, tmp);
      arcPoint((i + 1) / ARC, tmp2);
      const shimmer = 0.5 + 0.5 * Math.sin(i * 0.5 - time * 6);
      arc.setSegment(i, tmp, tmp2, palette.cyan, state.arcAlpha * (0.25 + shimmer * 0.25));
    }
    arc.flush(drawn);

    // Packets
    for (let f = FLIGHTS - 1; f >= 0; f--) {
      const flight = flights[f];
      if (!flight) continue;
      flight.t += dt / flight.duration;
      if (flight.t >= 1) {
        flights.splice(f, 1);
        if (flight.onArrive) flight.onArrive();
      }
    }
    for (let i = 0; i < packets.count; i++) packets.alpha[i] = 0;
    flights.forEach((flight, f) => {
      const head = G.ease(Math.min(flight.t, 1));
      for (let j = 0; j < TRAIL; j++) {
        const i = f * TRAIL + j;
        const p = Math.max(0, head - j * 0.022);
        arcPoint(flight.fromVault ? 1 - p : p, tmp);
        packets.setPosition(i, tmp);
        packets.setColor(i, flight.color, 1 - j / TRAIL * 0.5);
        packets.size[i] = flight.size * (1 - j / TRAIL * 0.7);
        packets.alpha[i] = (1 - j / TRAIL) * Math.min(1, flight.t * 8);
      }
    });
    packets.flush();

    // Threats
    if (state.threats && state.origin >= 0) {
      state.threatClock += dt;
      while (state.threatClock > 0.4) {
        state.threatClock -= 0.4;
        const t = threatState.find(item => item.life <= 0);
        if (t) {
          const a = rand(0, Math.PI * 2);
          const r = rand(1.1, 1.5);
          t.pos.set(Math.cos(a) * r, Math.sin(a) * r, rand(0.05, 0.4)).add(nodes[state.origin]);
          t.vel.copy(nodes[state.origin]).sub(t.pos).normalize().multiplyScalar(rand(0.8, 1.1));
          t.life = 3;
          t.bounced = false;
        }
      }
    }
    threatState.forEach((t, i) => {
      if (t.life <= 0) {
        threats.alpha[i] = 0;
        return;
      }
      t.life -= dt;
      t.pos.addScaledVector(t.vel, dt);
      if (!t.bounced && t.pos.distanceTo(nodes[state.origin] || t.pos) < 0.44 * Math.max(state.shield, 0.3)) {
        const normal = tmp.copy(t.pos).sub(nodes[state.origin]).normalize();
        t.vel.reflect(normal).multiplyScalar(0.6);
        t.bounced = true;
        t.life = Math.min(t.life, 1);
        state.flash = 1;
      }
      threats.setPosition(i, t.pos);
      threats.alpha[i] = Math.min(1, t.life) * (t.bounced ? 0.5 : 0.95);
    });
    threats.flush();

    // Pulses
    pulses.forEach(p => {
      if (p.t >= 1) {
        p.ring.visible = false;
        return;
      }
      p.t = Math.min(1, p.t + dt / 1.2);
      p.ring.scale.setScalar(0.15 + G.ease(p.t) * p.size);
      p.ring.material.opacity = (1 - p.t) * 0.8;
    });

    // Labels
    nodes.forEach((n, i) => {
      const depth = G.placeLabel(stage, codeLabels[i], root, n);
      codeLabels[i].classList.toggle("hide", i === state.origin);
      codeLabels[i].classList.toggle("back", depth < -1.2);
    });
    if (state.origin >= 0) {
      G.placeLabel(stage, originLabel, root, tmp.copy(nodes[state.origin]).add(LABEL_LIFT));
      G.placeLabel(stage, sealLabel, root, tmp.copy(nodes[state.origin]).sub(LABEL_LIFT));
    }
    G.placeLabel(stage, vaultLabel, root, tmp.copy(VAULT).add(new V3(0, 0, 0.78)));
  });

  // With reduced motion there is no loop, so advance the scene and draw one frame
  const settle = () => stage.refresh(150);

  return {
    raise(index) {
      const [code, city] = STATES[index];
      state.origin = index;
      state.shieldTarget = 0;
      state.shield = 0;
      state.arcDrawn = 0;
      state.arcDraw = false;
      state.arcAlpha = 0;
      state.arcAlphaTarget = 1;
      state.threats = false;
      flights.length = 0;

      shield.position.copy(nodes[index]);
      arcFrom.copy(nodes[index]).setZ(0.05);
      arcControl.copy(arcFrom).lerp(VAULT, 0.5).setZ(VAULT.z + 1.1);

      originLabel.innerHTML = `<span>${city} <small>${code}</small></span>`;
      originLabel.classList.remove("hide");
      sealLabel.classList.add("hide");
      pulse(nodes[index], palette.amber, 1.1);
      settle();
    },

    shield() {
      state.shieldTarget = 1;
      sealLabel.classList.remove("hide");
      pulse(nodes[state.origin], palette.violet, 0.7);
      settle();
    },

    send() {
      state.arcDraw = true;
      fly(false, 1.5, palette.white, 0.2, () => pulse(VAULT, palette.cyan, 1.2));
      settle();
    },

    ack() {
      fly(true, 1, palette.cyan, 0.13, () => pulse(nodes[state.origin], palette.cyan, 0.6));
      settle();
    },

    message(fromVault) {
      state.threats = true;
      fly(fromVault, 0.9, fromVault ? palette.violet : palette.white, 0.12);
      settle();
    },

    feedback() {
      fly(true, 1.3, palette.cyan, 0.2, () => pulse(nodes[state.origin], palette.cyan, 1));
      settle();
    },

    close() {
      state.threats = false;
      state.shieldTarget = 0;
      state.arcAlphaTarget = 0.35;
      sealLabel.classList.add("hide");
      fly(false, 1.2, palette.amber, 0.14, () => {
        archived = Math.min(ARCHIVE, archived + 1);
        pulse(VAULT, palette.violet, 1);
      });
      settle();
    }
  };
}
